/**
 * Shared tokens and copy for the public marketing pages (landing, /features).
 *
 * The marketing surface is the light design signed off against Anthony's mockups,
 * and that is what every visitor sees by default. AN-51 (Anthony, 9 Oct: "if it's
 * done one place, it's done for everything") reverses the old rule that it stayed
 * light: someone who has chosen Dark gets dark here too, through `dark:` variants
 * beside each raw colour.
 */
import { trialLength } from "./trial";

export const HERO_BLUE = "#2196F3";

/** Page band background used between white sections. */
export const BAND_BG = "#F7FAFD";
/** The band as classes, so it follows Dark (AN-51); an inline style cannot. */
export const BAND_CLASS = "bg-[#F7FAFD] dark:bg-background";

/**
 * The one support address. Inbound works — fomofi.app has an Outlook MX record
 * — which is why it stays offered as the route that reaches a person while
 * outbound is still dead (see pages/Contact.tsx).
 *
 * The note that used to sit here said fomofi.app had an MX record but NO A
 * record, so the bare `https://fomofi.app` links in the legal copy did not
 * resolve. That is NO LONGER TRUE as of 2026-09-22: fomofi.app resolves to
 * 100.60.217.246 — the same origin as fomofi.benmore.tech — serves HTTP 200, and
 * carries a Let's Encrypt certificate issued 2026-09-11. www redirects to the
 * apex. The legal links resolve, and the branded domain is what QA now files
 * against.
 */
export const SUPPORT_EMAIL = "support@fomofi.app";

/**
 * Pricing, shared by the landing's pricing section and the /features trial CTA
 * so the two can't drift. Any price change happens here once.
 *
 * SAVINGS WORDING — reversed 2026-07-31, on Anthony's call. This used to read
 * "2 months free", and the note here used to forbid a percentage. Anthony's
 * objection is about meaning, not arithmetic: readers take "2 months free" to
 * mean two unbilled months at the start of the subscription, which is not what
 * happens. "Save N%" is the percentage form of the same discount.
 *
 * The numbers, so nobody has to re-derive them: 12 × $10.99 = $131.88 against
 * $109/yr, a $22.88 saving = 17.35%, which rounds to 17%.
 *
 * AN-02 / AN-03 (Anthony's batch, 9 Oct; the new Terms of Service say the same):
 * $10.99 a month and $109 a year, replacing $11.99 / $119.99. SHIPS ONLY WITH THE
 * STRIPE PRICES (AN-06): the site must never quote a price Checkout doesn't charge.
 * AN-04 (Paul, 2026-10-12): the saving reads "2 months free when you pay yearly".
 * It's Anthony's phrase, and "when you pay yearly" avoids the July misreading above
 * (two unbilled months up front). The number is derived (ANNUAL_DISCOUNT.monthsFree),
 * so a re-price can't leave it stale.
 */
// The two prices, once. BILLING_CYCLES and ANNUAL_DISCOUNT both read them, so the
// saving label below is derived rather than typed (contract batch 10a).
const MONTHLY_PRICE = "$10.99";
const ANNUAL_PRICE = "$109.00";

/** "$119.99" / "$119.99/year" -> 119.99. Same parse the pricing guard uses. */
function priceOf(s: string): number {
  const m = s.replace(/,/g, "").match(/\d+(?:\.\d+)?/);
  if (!m) throw new Error(`no amount in ${JSON.stringify(s)}`);
  return Number(m[0]);
}

/**
 * The annual discount, DERIVED from the two configured prices above.
 *
 * BEN-141, Vinnie item 3. He reported the annual card (then $119.99/year) as
 * having "no savings framing". The framing existed — `saveLabel` — but it renders beside the
 * Monthly/Annual segmented control, a row above and horizontally apart from the
 * price it qualifies, so reading the card alone it genuinely is absent. Fixed by
 * stating it on the card too, from this one derivation.
 *
 * Derived rather than written down: the failure mode this guards is someone
 * changing the prices and leaving a literal percentage behind. The 2026-09-24
 * re-price (on hold, see above) is the case in point: the live pair and #844's
 * proposed pair both land on ~16.6%, and $8.99 / $95.99 lands on 11.02%, so a
 * remembered "17%" would survive that change while ceasing to be true.
 *
 * `pct` is rounded for display the way the badge already rounds (16.60 -> 17).
 */
export const ANNUAL_DISCOUNT = (() => {
  const monthly = priceOf(MONTHLY_PRICE);
  const annual = priceOf(ANNUAL_PRICE);
  // Rounded to the cent: 12 × 10.99 is 131.88000000000002 in floating point.
  const undiscounted = Math.round(monthly * 12 * 100) / 100;
  const saving = Math.round((undiscounted - annual) * 100) / 100;
  return {
    /** The monthly price as a number (10.99). */
    monthly,
    /** The annual price as a number (109). */
    annual,
    /** 12 × the monthly price — what a year of monthly billing costs. */
    undiscounted,
    /** Dollars saved per year by paying annually. */
    saving,
    /** Whole-percent saving (no longer the label; kept for the arithmetic). */
    pct: Math.round((saving / undiscounted) * 100),
    /** Whole months of the monthly price the annual plan saves: 22.88 / 10.99 -> 2. */
    monthsFree: Math.floor(saving / monthly + 1e-9),
  };
})();

/** AN-04: the one saving phrase, everywhere it's stated. Derived, never typed. */
export const ANNUAL_SAVING_COPY = `${ANNUAL_DISCOUNT.monthsFree} ${
  ANNUAL_DISCOUNT.monthsFree === 1 ? "month" : "months"
} free when you pay yearly`;

export const BILLING_CYCLES = {
  monthly: {
    label: "Monthly",
    price: MONTHLY_PRICE,
    unit: "/month",
    detail: "Billed monthly.",
    saveLabel: null,
    trialFooter: `${trialLength()} free trial, then ${MONTHLY_PRICE}/month. Cancel anytime.`,
  },
  annual: {
    label: "Annual",
    price: ANNUAL_PRICE,
    unit: "/year",
    // Was "Just $10.00/month, billed annually." Anthony: the derived
    // per-month figure alongside a yearly charge "doesn't mathematically add
    // up" as messaging, so the card now states only what it bills.
    detail: "Billed annually.",
    // Derived (contract batch 10a): a typed "17%" would survive a re-price.
    saveLabel: ANNUAL_SAVING_COPY,
    trialFooter: `${trialLength()} free trial, then ${ANNUAL_PRICE}/year. Cancel anytime.`,
  },
} as const;

export type BillingCycle = keyof typeof BILLING_CYCLES;

// Annual is the default so the savings badge is visible on load (standard
// pricing-page pattern, and it's the plan we want to steer people to).
export const DEFAULT_CYCLE: BillingCycle = "annual";

/**
 * Security claims — the exact strings the landing's Security section ships.
 *
 * DO NOT reword these. The original copy overstated encryption ("AES-256") and
 * response timeframes and was softened after review in #557/#558; the wording
 * below is what survived that pass. Anthony's features mockup captions this
 * strip "256-bit encryption", which is the same claim that was removed — it is
 * deliberately not reproduced here. Any change goes back through counsel.
 */
export const SECURITY_PILLARS = [
  {
    id: "bank-level",
    title: "Bank-level security",
    body: "Your financial information is encrypted in transit and at rest, and we never see or store your bank login credentials.",
    /** Short form for the compact trust strip; a truncation, not a new claim. */
    strip: "Encrypted in transit and at rest",
  },
  {
    id: "read-only",
    title: "Secure, read-only connections",
    body: "We use trusted, secure connections to your financial institutions. We never see your login, and you can disconnect linked accounts at any time.",
    strip: "We never see your bank login",
  },
  {
    id: "never-sold",
    title: "Your data is never sold",
    body: "We don't sell, rent, or share your financial data with advertisers or data brokers. Your money, your information.",
    strip: "Never sold to advertisers or brokers",
  },
] as const;

/**
 * Intrinsic pixel dimensions of the marketing screenshots.
 *
 * These images are unsized in markup otherwise, which made every section below
 * them shift as they decoded — harmless while nothing deep-linked into the
 * landing, but it lands cross-page anchor jumps in the wrong place (Phase 1
 * finding). Giving each <img> a width/height reserves the box up front.
 */
export const MARKETING_IMAGE_SIZES: Record<string, { width: number; height: number }> = {
  "/screenshots/dashboard-hero.png": { width: 1904, height: 1042 },
  "/screenshots/bill-calendar.png": { width: 1913, height: 1034 },
  "/screenshots/debt-eliminator.png": { width: 1907, height: 1041 },
  "/screenshots/connected-banks.png": { width: 1920, height: 1042 },
  "/screenshots/phone-hero.png": { width: 1545, height: 1999 },
};
